"use client";

import { useHyperliquidAbstractionModeMutation } from "@cede/trading-data";
import { useSignTypedDataWithConnection } from "@/hooks/core/useSignTypedDataWithConnection";
import { isWalletActionError, WALLET_ACTION_ERROR_CODE } from "@/hooks/core/useWalletActionGuard";
import * as Sentry from "@sentry/nextjs";
import { enqueueSnackbar } from "notistack";

/**
 * Hub wrapper around the package `useHyperliquidAbstractionModeMutation`.
 *
 * Injects the wallet-connection-aware signer and the hub-specific side effects
 * (success snackbar, Sentry capture + error snackbar with user-rejection filtering)
 * so call sites get the full read + mutate abstraction-mode object.
 */
export function useHyperliquidUnifiedAccountSetup(userAddress: string | undefined, enabled = true) {
  const { signTypedDataAsync } = useSignTypedDataWithConnection();

  return useHyperliquidAbstractionModeMutation({
    userAddress,
    enabled,
    signTypedDataAsync,
    onEnableSuccess: () => {
      enqueueSnackbar("Unified account enabled", { variant: "success" });
    },
    onEnableError: (error) => {
      if (isWalletActionError(error) && error.code === WALLET_ACTION_ERROR_CODE.USER_REJECTED) {
        return;
      }
      Sentry.captureException(error);
      enqueueSnackbar(`Unified account setup failed: ${error.message}`, { variant: "error" });
    },
  });
}
